import { http, HttpError } from "./http";

/**
 * Pull the most useful human message out of an upload error response, falling
 * back to the HTTP status text. Handles the API's `{ error }` shape, ASP.NET
 * Core's ProblemDetails (`title`/`detail`/`errors`) and a short snippet of any
 * non-JSON body so we never just show "Bad Request".
 */
function formatUploadError(body: string, status: number, statusText: string): string {
  if (body && body.length > 0) {
    try {
      const j = JSON.parse(body);
      if (j && typeof j === "object") {
        if (typeof (j as { error?: unknown }).error === "string") {
          return (j as { error: string }).error;
        }
        const detail = (j as { detail?: unknown }).detail;
        if (typeof detail === "string" && detail.trim()) return detail;
        const title = (j as { title?: unknown }).title;
        if (typeof title === "string" && title.trim()) {
          return status > 0 ? `${title} (HTTP ${status})` : title;
        }
        const errors = (j as { errors?: Record<string, unknown> }).errors;
        if (errors && typeof errors === "object") {
          const flat: string[] = [];
          for (const k of Object.keys(errors)) {
            const v = errors[k];
            if (Array.isArray(v)) flat.push(...v.map(String));
            else if (typeof v === "string") flat.push(v);
          }
          if (flat.length) return flat.join(" ");
        }
      }
    } catch {
      const trimmed = body.trim().replace(/\s+/g, " ").slice(0, 240);
      if (trimmed.length > 0) {
        return status > 0 ? `${statusText} (HTTP ${status}): ${trimmed}` : trimmed;
      }
    }
  }
  return status > 0 ? `${statusText} (HTTP ${status})` : statusText;
}

export interface VoiceItem {
  voiceId: number;
  applicationId?: number | null;
  name: string;
  transcription: string;
  allowRecord: boolean;
  hasVoiceFile: boolean;
  createdUtc: string;
  modifiedUtc: string;
}

export interface VoiceListResponse {
  items: VoiceItem[];
}

export interface SpeechPreset {
  id: string;
  label: string;
}

export interface SpeechPresetsResponse {
  items: SpeechPreset[];
}

export interface SpeechPreviewVariant {
  audio: string;
}

export interface SpeechPreviewResponse {
  variants: SpeechPreviewVariant[];
}

export interface TranscribeAudioResponse {
  transcript: string;
  detectedLanguage: string;
  languageConfidence?: number | null;
  confidence?: number | null;
}

function listBase(applicationId: number | null): string {
  return applicationId != null
    ? `/api/applications/${applicationId}/voices`
    : "/api/voices";
}

function presetsUrl(applicationId: number | null): string {
  return applicationId != null
    ? `/api/applications/${applicationId}/voices/options/speech-presets`
    : "/api/voices/options/speech-presets";
}

export function voiceAudioUrl(
  applicationId: number | null,
  voiceId: number,
  bust?: number,
): string {
  const q = bust != null ? `?t=${bust}` : "";
  return `${listBase(applicationId)}/${voiceId}/audio${q}`;
}

export const voicesApi = {
  list: (applicationId: number | null) =>
    http<VoiceListResponse>(listBase(applicationId)),

  speechPresets: (applicationId: number | null) =>
    http<SpeechPresetsResponse>(presetsUrl(applicationId)),

  create: (applicationId: number | null, payload: { name: string; transcription?: string }) =>
    http<{ voiceId: number }>(listBase(applicationId), {
      method: "POST",
      body: JSON.stringify({
        name: payload.name,
        transcription: payload.transcription ?? "",
      }),
    }),

  update: (
    applicationId: number | null,
    voiceId: number,
    payload: { name: string; transcription: string; allowRecord?: boolean },
  ) =>
    http<VoiceItem>(`${listBase(applicationId)}/${voiceId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  delete: (applicationId: number | null, voiceId: number) =>
    fetch(`${listBase(applicationId)}/${voiceId}`, {
      method: "DELETE",
      credentials: "include",
    }).then(async (r) => {
      if (!r.ok) {
        const text = await r.text();
        let msg = r.statusText;
        try {
          const j = JSON.parse(text);
          if (j && typeof j.error === "string") msg = j.error;
        } catch {
          /* ignore */
        }
        throw new HttpError(msg, r.status, text);
      }
    }),

  uploadAudio: async (
    applicationId: number | null,
    voiceId: number,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void },
  ): Promise<void> => {
    const url = `${listBase(applicationId)}/${voiceId}/audio`;
    const onUploadProgress = options?.onUploadProgress;

    if (onUploadProgress == null) {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(url, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!r.ok) {
        const text = await r.text();
        throw new HttpError(formatUploadError(text, r.status, r.statusText), r.status, text);
      }
      return;
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) {
          onUploadProgress(Math.min(100, Math.round((100 * e.loaded) / e.total)));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
          return;
        }
        const msg = formatUploadError(xhr.responseText, xhr.status, xhr.statusText || "Upload failed");
        reject(new HttpError(msg, xhr.status, xhr.responseText));
      };
      xhr.onerror = () => reject(new HttpError("Network error", 0, ""));
      const fd = new FormData();
      fd.append("file", file);
      xhr.send(fd);
    });
  },

  speechPreview: (
    applicationId: number | null,
    voiceId: number,
    payload: { text: string; presetId: string },
  ) =>
    http<SpeechPreviewResponse>(`${listBase(applicationId)}/${voiceId}/speech-preview`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /**
   * Hidden feature: ask the API to transcribe the saved audio for a voice via
   * Deepgram. Pass a BCP-47 language code (e.g. "nl", "en", "fr", "de") to
   * skip auto-detect and use a single explicit-language pass — recommended
   * because Nova-3 can silently drop chunks of non-English audio. Omit the
   * language to fall back to the auto-detect + Nova-2 gap-fill pipeline.
   */
  transcribeAudio: (applicationId: number | null, voiceId: number, language?: string) => {
    const qs = language ? `?language=${encodeURIComponent(language)}` : "";
    return http<TranscribeAudioResponse>(
      `${listBase(applicationId)}/${voiceId}/transcribe-audio${qs}`,
      { method: "POST" },
    );
  },
};
